import { Component, signal, inject, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ApiService } from './api.service';

@Component({
  selector: 'app-activate',
  standalone: true,
  imports: [FormsModule, CommonModule],
  template: `
    <div class="min-h-screen flex font-sans" style="background:#f1f9fe;">
      <!-- Left Banner (Identical to Login) -->
      <div class="hidden lg:flex lg:w-1/2 relative overflow-hidden flex-col items-center justify-center p-12" style="background:linear-gradient(145deg, #0C3A5A 0%, #1A6FA6 50%, #2B9AD2 100%);">
        <div class="absolute -top-20 -left-20 w-72 h-72 rounded-full opacity-10" style="background:#5EC9F0;"></div>
        <div class="absolute -bottom-32 -right-16 w-96 h-96 rounded-full opacity-10" style="background:#F6AF26;"></div>
        <div class="relative z-10 text-center">
          <h2 class="text-white text-3xl font-bold mb-4 leading-snug">Welcome to the Team</h2>
          <p class="text-white/70 text-base max-w-sm mx-auto">Activate your staff profile and set up your secure password to access the Command Center.</p>
        </div>
      </div>

      <!-- Right Form -->
      <div class="flex-1 flex items-center justify-center p-8">
        <div class="w-full max-w-md">
          <div class="bg-white rounded-3xl p-8 shadow-sm" style="border:1px solid #EEF2F7;">
            <h1 class="text-2xl font-bold text-slate-900 mb-1">Activate Account</h1>
            <p class="text-slate-500 text-sm mb-6">Hello {{ userName() }}, please set your password below.</p>

            @if (errorMsg()) {
              <div class="p-3.5 mb-5 rounded-2xl flex items-center gap-3 text-sm font-medium" style="background:#FEF2F2; color:#EF4444; border:1px solid #FECACA;">
                <i class="ti ti-alert-circle text-[18px] shrink-0"></i>{{ errorMsg() }}
              </div>
            }

            @if (successMsg()) {
              <div class="p-3.5 mb-5 rounded-2xl flex items-center gap-3 text-sm font-medium" style="background:#ECFDF5; color:#059669; border:1px solid #A7F3D0;">
                <i class="ti ti-circle-check text-[18px] shrink-0"></i>{{ successMsg() }}
              </div>
            }

            @if (isValidToken()) {
              <form (submit)="onSubmit()" class="space-y-5">
                <div>
                  <label class="block text-sm font-semibold text-slate-700 mb-1.5">New Password</label>
                  <input type="password" [(ngModel)]="password" name="password" placeholder="••••••••" class="w-full h-12 px-4 rounded-2xl text-sm border outline-none" style="border-color:#E2E8F0; background:#FAFBFC;">
                </div>
                <div>
                  <label class="block text-sm font-semibold text-slate-700 mb-1.5">Confirm Password</label>
                  <input type="password" [(ngModel)]="confirmPassword" name="confirmPassword" placeholder="••••••••" class="w-full h-12 px-4 rounded-2xl text-sm border outline-none" style="border-color:#E2E8F0; background:#FAFBFC;">
                </div>
                <button type="submit" class="w-full h-12 rounded-2xl font-bold text-white shadow-sm hover:opacity-90 transition-all flex items-center justify-center" style="background:linear-gradient(135deg, #2B9AD2, #1A6FA6);">
                  Set Password
                </button>
              </form>
            } @else {
              <p class="text-slate-400 text-sm">Verifying activation token...</p>
            }
          </div>
        </div>
      </div>
    </div>
  `
})
export class ActivateComponent implements OnInit {
  token = '';
  userName = signal('');
  password = '';
  confirmPassword = '';
  isValidToken = signal(false);
  errorMsg = signal('');
  successMsg = signal('');

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private api = inject(ApiService);

  ngOnInit() {
    this.token = this.route.snapshot.queryParamMap.get('token') || '';
    if (this.token) {
      this.verifyToken();
    } else {
      this.errorMsg.set('Invalid link. Token missing.');
    }
  }

  verifyToken() {
    this.api.verifyActivateToken(this.token).subscribe({
      next: (res: any) => {
        if (res.success && res.data) {
          this.userName.set(res.data.name);
          this.isValidToken.set(true);
        } else {
          this.errorMsg.set(res.message || 'Invalid activation token.');
        }
      },
      error: (err: any) => {
        this.errorMsg.set('Token validation failed or expired.');
      }
    });
  }

  onSubmit() {
    if (!this.password) {
      this.errorMsg.set('Password is required.');
      return;
    }
    if (this.password !== this.confirmPassword) {
      this.errorMsg.set('Passwords do not match.');
      return;
    }

    this.api.submitActivate({ token: this.token, password: this.password }).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.successMsg.set('Password set successfully! Redirecting to login...');
          setTimeout(() => this.router.navigate(['/login']), 3000);
        } else {
          this.errorMsg.set(res.message || 'Activation failed.');
        }
      },
      error: (err: any) => {
        this.errorMsg.set('An error occurred. Please try again.');
      }
    });
  }
}
